/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  View,
  Modal,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Dimensions,
} from 'react-native';
import Toast from 'react-native-toast-message';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';

import * as Layout from '@components/screen/Layout';
import SociollaText from '@components/sociolla/SociollaText';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import SelectBox from '@components/form/SelectBox';
import RadioButton from '@components/form/RadioButton';
import CustomerSearch from '@components/form/search/CustomerSearch';
import SalesTeamSearch from '@components/form/search/SalesTeamSearch';
import TitleSubSection from '@components/global/TitleSubSection';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import RenderIf from '@components/global/RenderIf';
import SociollaTable from '@components/sociolla/SociollaTable';
import ProgressBar from '@components/global/ProgressBar';

import { useNavigation, useFocusEffect } from '@react-navigation/native';
import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import { MsAccount } from '@api/ms-account';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import Colors from '@global/colors';
import { isWeb, isMobileWeb } from '@global/screens';
import Image from '@components/global/Image';
import ModalProduct from './components/ModalProduct';
import ModalProductExist from './components/ModalProductExist';
import { BA_MERCHANDISER_ROLES, BASE_ROLES } from './constants';
import { escapeRegexChars, getAttibutes } from '@global/helpers';
import { translate } from '@global/translate';

const TOTAL_PAGE = 2;

const defaultPhoto = require('@/assets/img/default/user-default.png');

const HEADER_DEKSTOP_EDIT = [
  {
    label: 'Image',
    data: 'image',
    type: 'image',
    width: '10%',
    styleItem: {
      alignItems: 'center',
      padding: 0,
      margin: 0,
    },
  },
  {
    label: 'Brand Name',
    data: 'brand_name',
    width: '15%',
  },
  {
    label: 'Product Name',
    data: 'ref_name',
    width: '25%',
  },
  {
    label: 'Attribute',
    data: 'variant',
    width: '15%',
  },
  {
    label: 'Category',
    data: 'category_name',
    width: '20%',
  },
  {
    label: 'Qty',
    data: 'quantity',
    width: '10%',
  },
  {
    label: '',
    data: '',
    type: 'action',
    action: [
      {
        icon: require('@assets/img/icons/trash-grey.svg'),
        name: 'remove',
      },
    ],
    width: '5%',
  },
];

const getNextStatus = (status) => {
  if (status === 'need_approval') {
    return 'approved';
  }
  return status;
};

const getSafeId = (val) => {
  if (!val) {
    return null;
  }
  if (typeof val === 'string') {
    return val;
  }
  if (val.$oid) {
    return val.$oid;
  }
  if (val.id) {
    return getSafeId(val.id);
  }
  if (val._id) {
    return getSafeId(val._id);
  }
  return null;
};

const ContentWrapper = ({ fill = false, children }) => {
  if (isWeb) {
    return (
      <Layout.Wrapper rounded hasProgressBar>
        {children}
      </Layout.Wrapper>
    );
  }
  // `fill` bounds the box to the height the sticky buttons leave over, so the
  // form's ScrollView has something to scroll against. Page 2 keeps the auto
  // height: its product table is a sibling and flex would starve it.
  const fillStyle = fill ? { flex: 1 } : null;
  return (
    <View style={fillStyle}>
      <Layout.Wrapper
        rounded
        hasProgressBar
        minHeight={1}
        isUndefined
        style={fillStyle || {}}>
        {children}
      </Layout.Wrapper>
    </View>
  );
};

// Web scrolls at the page container; mobile has to scroll the form itself.
const FormBody = ({ style, children }) => {
  if (isWeb) {
    return <Layout.FormWrapper style={style}>{children}</Layout.FormWrapper>;
  }
  if (isMobileWeb) {
    return (
      <View style={{ height: Dimensions.get('window').height - 190 }}>
        <ScrollView
          keyboardShouldPersistTaps="handled"
          style={{ flex: 1 }}
          contentContainerStyle={{ width: '100%', ...style }}>
          {children}
        </ScrollView>
      </View>
    );
  }
  return (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={{ width: '100%', ...style }}>
      {children}
    </ScrollView>
  );
};

const RequestProductFormScreen = (props) => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const formRef = useRef();
  const tableRef = useRef();

  const { id } = props.route?.params || {};
  const isEdit = !!id;

  const userRole = user?.role?.code;
  const isBAOrMerchandiser = BA_MERCHANDISER_ROLES.includes(userRole);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [products, setProducts] = useState([]);
  const [productCount, setProductCount] = useState(0);
  const [paramsProduct, setParamsProduct] = useState({
    filter: {},
    limit: isWeb ? 10 : 15,
    skip: 0,
  });
  const [loadingMore, setLoadingMore] = useState(false);

  const [detailData, setDetailData] = useState(null);

  const [usersOptions, setUsersOptions] = useState([]);
  const [usersSkip, setUsersSkip] = useState(0);
  const usersLimit = 10;
  const [isLoadMoreUsers, setIsLoadMoreUsers] = useState(false);
  const [loadMoreUsersEmpty, setLoadMoreUsersEmpty] = useState(false);
  const [usersKeyword, setUsersKeyword] = useState('');

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showProductExistModal, setShowProductExistModal] = useState(false);
  const [conflictingProducts, setConflictingProducts] = useState([]);
  const [rejectNote, setRejectNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [outletOptions, setOutletOptions] = useState([]);
  const [addressOptions, setAddressOptions] = useState([]);
  const [addressType, setAddressType] = useState(null);
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [storeAddresses, setStoreAddresses] = useState([]);

  const [newRptId, setNewRptId] = useState(null);

  const getRequestProductTesterDetail = useApi(
    MsOrder.getRequestProductTesterDetail,
  );
  const getCustomerId = useApi(MsAccount.getCustomerId);
  const getUsers = useApi(MsAccount.getUsers);
  const getOutletsByCustomerId = useApi(MsAccount.getOutletsByCustomerId);

  const hasSalesTeam = !!user?.sales_team?.length;
  const canSelectUser = !hasSalesTeam && !isBAOrMerchandiser;

  const userSalesTeam = user?.sales_team || [];
  const isMT = (customer) =>
    customer?.market_type?.code?.toLowerCase() === 'mt';

  const addressTypeOptions = [
    { label: 'Delivery Address', value: 'delivery' },
    { label: 'Store Address', value: 'store' },
  ];

  const initialValues = useMemo(
    () => ({
      customer: null,
      outlet: null,
      address: null,
      sales_team: null,
      request_by: canSelectUser ? null : user,
    }),
    [canSelectUser, user],
  );

  const validationSchema = Yup.object().shape({
    customer: Yup.object().required('Customer is required'),
    outlet: Yup.mixed().when('customer', {
      is: (customer) => customer?.is_sub_distributor,
      then: Yup.object().required('Outlet is required'),
      otherwise: Yup.mixed().nullable(),
    }),
    address: Yup.mixed().required('Address is required'),
    sales_team: Yup.mixed().test(
      'required-sales-team',
      'Sales team is required.',
      function (value) {
        return !!value?.id;
      },
    ),
    request_by: Yup.mixed().test(
      'required-request-by',
      'Request by is required',
      function (value) {
        if (canSelectUser && !value) {
          return false;
        }
        return true;
      },
    ),
  });

  useEffect(() => {
    if (canSelectUser) {
      fetchUsers();
    }
  }, [canSelectUser]);

  const fetchUsers = async (skip = 0, keyword = '', append = false) => {
    if (append) {
      setIsLoadMoreUsers(true);
    }
    try {
      const params = {
        params: {
          limit: usersLimit,
          skip: skip,
          filter: {
            'role.code': {
              $in: BA_MERCHANDISER_ROLES,
            },
          },
        },
      };

      if (keyword) {
        params.params.filter['name'] = { $regex: keyword, $options: 'i' };
      }

      const res = await getUsers.request(params);
      if (res?.data?.data) {
        const newUsers = res.data.data.map((u) => ({
          ...u,
          label: u.name,
          value: getSafeId(u.id || u._id),
          id: getSafeId(u.id || u._id),
        }));

        if (append) {
          setUsersOptions((prev) => [...prev, ...newUsers]);
        } else {
          setUsersOptions(newUsers);
        }

        if (newUsers.length < usersLimit) {
          setLoadMoreUsersEmpty(true);
        } else {
          setLoadMoreUsersEmpty(false);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadMoreUsers(false);
    }
  };

  const handleSearchUsers = (keyword) => {
    setUsersKeyword(keyword);
    setUsersSkip(0);
    setLoadMoreUsersEmpty(false);
    fetchUsers(0, keyword, false);
  };

  const handleLoadMoreUsers = () => {
    if (!loadMoreUsersEmpty && !isLoadMoreUsers) {
      const newSkip = usersSkip + usersLimit;
      setUsersSkip(newSkip);
      fetchUsers(newSkip, usersKeyword, true);
    }
  };

  const resetState = useCallback(() => {
    setProducts([]);
    setDetailData(null);
    setSelectedCustomer(null);
    setSelectedOutlet(null);
    setOutletOptions([]);
    setAddressOptions([]);
    setDeliveryAddresses([]);
    setStoreAddresses([]);
    setAddressType(null);
    setRejectNote('');
    setShowRejectModal(false);
    setActionLoading(false);
    setLoading(false);
    setUsersOptions([]);
    setUsersKeyword('');
    setUsersSkip(0);
    setLoadMoreUsersEmpty(false);
    setProductCount(0);
    setParamsProduct({
      filter: {},
      limit: isWeb ? 10 : 15,
      skip: 0,
    });
    setLoadingMore(false);
    setNewRptId(null);
    if (formRef.current) {
      formRef.current.resetForm();
      formRef.current.setValues(initialValues);
    }
  }, [initialValues]);

  useEffect(() => {
    navigation.setOptions({
      headerRight: () => {
        return isWeb ? <NotificationSettingButton {...props} /> : false;
      },
    });
  }, [props]);

  useEffect(() => {
    if (id) {
      fetchDetail(id);
    }
  }, [id]);

  useEffect(() => {
    if (props.route?.params?.openAddProduct) {
      setShowAddProduct(true);
      navigation.setParams({ openAddProduct: undefined });
    }
  }, [props.route?.params?.openAddProduct]);

  useFocusEffect(
    useCallback(() => {
      if (!id) {
        resetState();
      }
    }, [id, resetState]),
  );

  const fetchDetail = async (requestId) => {
    setLoading(true);
    try {
      const res = await getRequestProductTesterDetail.request(requestId);
      if (res?.data?.data) {
        const data = res.data.data;
        setDetailData(data);

        if (formRef.current) {
          formRef.current.setValues({
            customer: data.customer,
            outlet: data.outlet,
            address: data.address?.id,
            sales_team: data.customer?.sales_team?.id
              ? data.customer.sales_team
              : null,
            request_by: data.user
              ? {
                  ...data.user,
                  label: data.user.name,
                  value: getSafeId(data.user.id || data.user._id),
                  id: getSafeId(data.user.id || data.user._id),
                }
              : null,
            status: data.status,
            note: data.note,
          });
          setSelectedCustomer(data.customer);
          const outletObj = data.outlet;
          setSelectedOutlet(outletObj);

          let customersData = null;
          if (data.customer?.id) {
            customersData = await fetchCustomerDependencies(
              data.customer.id,
              data,
            );
          }

          if (data.address?.address_type) {
            setAddressType(data.address.address_type);
          } else {
            const isStore = customersData?.store_address?.some(
              (a) => a.id === data.address?.id || a._id === data.address?.id,
            );
            setAddressType(isStore ? 'store' : 'delivery');
          }
        }

        await fetchProducts(paramsProduct);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (params) => {
    setParamsProduct(params);
    try {
      let totalCount = productCount;
      if (params.skip == 0) {
        const countResponse =
          await MsOrder.getProductsCountFromRequestProductTester(id, {
            params,
          });
        if (countResponse && countResponse.data && countResponse.data.data) {
          totalCount = countResponse.data.data;
        } else {
          totalCount = 0;
        }
      }
      setProductCount(totalCount);

      const productsRes = await MsOrder.getProductsFromRequestProductTester(
        id,
        { params },
      );

      if (productsRes?.data?.data) {
        const mappedProducts = productsRes.data.data.map((item) => {
          let attributesStr = '-';
          if (item.combination?.attributes) {
            attributesStr = getAttibutes(item.combination.attributes);
          }

          return {
            ...item,
            product: {
              ...item.product,
              id: getSafeId(item.mongo_product_id),
              name: item.name,
              brand: item.brand,
              category: item.category,
              image: item.combination?.image || '',
              attributes: attributesStr,
              product_combination_id: getSafeId(item.combination),
            },
            combination: item.combination,
            quantity: item.quantity || 1,
            unit_name: item.unit_name,

            brand_name: item.brand.name,
            ref_name: `[${item.combination.reference}] ${item.name}`,
            variant: attributesStr,
            category_name: item.category?.name,
            image: item.combination.image,
          };
        });
        setProducts(mappedProducts);
      } else {
        setProducts([]);
      }
    } catch (prodErr) {
      setProducts([]);
    }
  };

  const onSearchTable = async (param, type) => {
    if (!id) {
      return;
    }
    let params = { ...paramsProduct };
    if (type === 'keyword') {
      if (param.keyword.length > 2) {
        params.filter['$or'] = [
          {
            name: { $regex: escapeRegexChars(param.keyword), $options: 'i' },
          },
          {
            'combination.reference': { $regex: param.keyword, $options: 'i' },
          },
          {
            'combination.ean_no': { $regex: param.keyword, $options: 'i' },
          },
          {
            'brand.name': { $regex: param.keyword, $options: 'i' },
          },
        ];
      } else if (!param.keyword) {
        delete params.filter['$or'];
      }
    } else if (type === 'pagination') {
      setLoadingMore(true);
      params.limit = param.limit;
      params.skip = param.skip;
    } else if (type === 'reset') {
      params = {
        filter: {},
        limit: isWeb ? 10 : 15,
        skip: 0,
      };
    }
    await fetchProducts({ ...params });
    setLoadingMore(false);
  };

  const onCloseProduct = () => {
    setShowAddProduct(false);
  };

  const formatAddress = (a) => {
    if (!a) {
      return '-';
    }
    const street = a.street || '';
    const street2 = a.street2 ? `, ${a.street2}` : '';
    const city = a.city?.name ? `, ${a.city.name}` : '';
    const state = a.state?.name ? `, ${a.state.name}` : '';
    const country = a.country?.name ? `, ${a.country.name}` : '';
    const postal = a.postal_code ? ` ${a.postal_code}` : '';
    const full = `${street}${street2}${city}${state}${country}${postal}`.trim();
    return full.startsWith(',') ? full.substring(1).trim() : full;
  };

  const fetchCustomerDependencies = async (customerId, detailData = null) => {
    try {
      const res = await getCustomerId.request(customerId);
      if (res?.data?.data) {
        const customerData = res.data.data;
        setSelectedCustomer(customerData);

        const fallbackAddressType = 'delivery';

        if (customerData.is_sub_distributor) {
          try {
            const outletsRes = await getOutletsByCustomerId.request(
              customerData.id || customerData._id,
            );
            if (outletsRes?.data?.data) {
              const mappedOutlets = outletsRes.data?.data?.map((o) => ({
                ...o,
                label: o.name,
                value: o._id,
              }));
              setOutletOptions(mappedOutlets);

              if (isEdit && detailData?.outlet) {
                const existingOutlet = mappedOutlets.find(
                  (o) => o.value === getSafeId(detailData.outlet.id),
                );

                if (existingOutlet) {
                  setSelectedOutlet(existingOutlet);
                  if (formRef.current) {
                    formRef.current.setFieldValue('outlet', existingOutlet);
                  }
                }
              }
            }
          } catch (error) {
            console.error(error);
          }
        } else if (customerData.outlets) {
          setOutletOptions(
            customerData.outlets.map((o) => ({
              ...o,
              label: o.name,
              value: o.id,
            })),
          );
        }

        const dAddresses = (customerData.delivery_address || []).map((a) => {
          const fullAddress = formatAddress(a);
          return {
            ...a,
            label: fullAddress,
            value: a._id,
            address: fullAddress,
            type: 'delivery',
          };
        });

        const sAddresses = (customerData.store_address || []).map((a) => {
          const fullAddress = formatAddress(a);
          return {
            ...a,
            label: fullAddress,
            value: a._id,
            address: fullAddress,
            type: 'store',
          };
        });

        const allAddresses = [...dAddresses, ...sAddresses];

        setDeliveryAddresses(dAddresses);
        setStoreAddresses(sAddresses);
        setAddressOptions(allAddresses);

        if (!isEdit && !customerData.is_sub_distributor) {
          setAddressType(fallbackAddressType);
          const targetList =
            fallbackAddressType === 'delivery' ? dAddresses : sAddresses;

          if (targetList.length > 0) {
            if (formRef.current) {
              formRef.current.setFieldValue('address', targetList[0].value);
            }
          }
        }
        return customerData;
      }
      return null;
    } catch (error) {
      console.error(error);
    }
  };

  const handleCustomerChange = (val, setFieldValue) => {
    setFieldValue('customer', val);
    setFieldValue('outlet', null);
    setFieldValue('address', null);
    setSelectedCustomer(val);
    setSelectedOutlet(null);
    setOutletOptions([]);
    setAddressOptions([]);
    setDeliveryAddresses([]);
    setStoreAddresses([]);
    setAddressType(null);

    if (isMT(val)) {
      setFieldValue(
        'sales_team',
        userSalesTeam?.length ? userSalesTeam[0] : null,
      );
    } else {
      setFieldValue('sales_team', val?.sales_team?.id ? val.sales_team : null);
    }

    if (val?._id) {
      fetchCustomerDependencies(val._id);
    }
  };

  const handleOutletChange = (val, setFieldValue) => {
    setFieldValue('outlet', val);
    setSelectedOutlet(val);

    if (selectedCustomer?.is_sub_distributor) {
      setFieldValue('address', val);
    }
  };

  const handleAddProductClick = async () => {
    if (!isEdit && formRef.current) {
      const errors = await formRef.current.validateForm();
      if (Object.keys(errors).length > 0) {
        const touchedErrors = Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {});
        formRef.current.setTouched(touchedErrors);
        return;
      }
    }
    setShowAddProduct(true);
  };

  const handleProductAdd = async (val) => {
    const payload = {
      product: val,
    };
    setShowAddProduct(false);

    const currentId = detailData?._id || id;

    if (currentId) {
      dispatch(showPopup({ name: 'isLoading', value: true }));
      setActionLoading(true);

      // Edit RPT first
      const payload = generatePayload(formRef.current.values);
      await MsOrder.updateRequestProductTester(currentId, payload);

      try {
        const productPayload = {
          product: {
            id: val.id,
            combination_id: val.product_combination_id,
          },
        };

        if (!val.id || !val.product_combination_id) {
          Toast.show({
            type: 'error',
            text1: 'Product ID or Combination ID is missing',
          });
          return;
        }
        await MsOrder.addProductToRequestProductTester(
          currentId,
          productPayload,
        );
        fetchDetail(currentId);
        Toast.show({
          type: 'success',
          text1: 'Product added successfully',
        });
      } catch (error) {
        const errorCode = error?.response?.data?.errorCode;
        if (errorCode === 'PRODUCT_ALREADY_EXISTS') {
          const errorData = error?.response?.data?.data?.data;
          if (errorData?.products) {
            setConflictingProducts(errorData.products);
            setShowProductExistModal(true);
          } else {
            Toast.show({
              type: 'error',
              text1: error?.response?.data?.message || 'Product already exists',
            });
          }
        } else {
          Toast.show({
            type: 'error',
            text1: error?.response?.data?.message || 'Failed to add product',
          });
        }
      } finally {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        setActionLoading(false);
      }
    } else {
      handleSave(formRef.current.values, true, [payload]);
    }
  };

  const handleRemoveProduct = async (index) => {
    const currentId = detailData?._id || id;
    const item = products[index];

    const removeItem = () => {
      const newProducts = [...products];
      newProducts.splice(index, 1);
      setProducts(newProducts);
      return newProducts;
    };

    if (!currentId) {
      removeItem();
      return;
    }

    dispatch(
      showAlert({
        show: true,
        title: 'Remove Product',
        description: 'Are you sure you want to remove this product?',
        onCallback: async () => {
          dispatch(showPopup({ name: 'isLoading', value: true }));
          setActionLoading(true);
          try {
            const safeCurrentId = getSafeId(currentId);
            const productId = item.id;
            const combinationId = getSafeId(
              item.product?.product_combination_id,
            );

            if (!productId || !combinationId || !safeCurrentId) {
              Toast.show({
                type: 'error',
                text1: 'Product information is incomplete',
              });
              return;
            }

            if (safeCurrentId && productId && combinationId) {
              await MsOrder.deleteProductFromRequestProductTester(
                safeCurrentId,
                productId,
                combinationId,
              );

              fetchDetail(safeCurrentId);
              Toast.show({
                type: 'success',
                text1: 'Product removed successfully',
              });
            } else {
              Toast.show({
                type: 'error',
                text1: 'Missing product information',
              });
            }
          } catch (error) {
            console.error(error);
            Toast.show({
              type: 'error',
              text1:
                error?.response?.data?.message || 'Failed to remove product',
            });
          } finally {
            dispatch(showPopup({ name: 'isLoading', value: false }));
            setActionLoading(false);
          }
        },
      }),
    );
  };

  const getAddressString = (customer, outlet, addressObj) => {
    if (customer?.is_sub_distributor) {
      if (typeof outlet?.address === 'string' && outlet?.address) {
        return outlet.address;
      }
      return formatAddress(outlet);
    }

    if (!addressObj) {
      return '-';
    }

    if (typeof addressObj?.address === 'string') {
      return addressObj.address;
    }

    if (addressObj?.label) {
      return addressObj.label;
    }

    return formatAddress(addressObj);
  };

  const handleRemoveConflictingProduct = async (item) => {
    const combinationIdToRemove = item.combination?.id;
    if (combinationIdToRemove) {
      const currentId = detailData?._id || id;
      const safeCurrentId = getSafeId(currentId);

      const matchingProductIndex = products.findIndex((p) => {
        const currentComboId = p.product?.product_combination_id;
        return currentComboId === combinationIdToRemove;
      });

      if (matchingProductIndex !== -1 && safeCurrentId) {
        const matchingProduct = products[matchingProductIndex];
        if (matchingProduct.id) {
          dispatch(showPopup({ name: 'isLoading', value: true }));
          try {
            const productId = matchingProduct.id;
            const combinationId = getSafeId(
              matchingProduct.product?.product_combination_id,
            );

            await MsOrder.deleteProductFromRequestProductTester(
              safeCurrentId,
              productId,
              combinationId,
            );

            const newProducts = [...products];
            newProducts.splice(matchingProductIndex, 1);
            setProducts(newProducts);
            setConflictingProducts((prev) =>
              prev.filter((p) => p.combination?.id !== combinationIdToRemove),
            );

            if (newProducts.length === 0 || conflictingProducts.length <= 1) {
              setShowProductExistModal(false);
            }

            Toast.show({
              type: 'success',
              text1: 'Product removed successfully',
            });
            return;
          } catch (error) {
            console.error(error);
            Toast.show({
              type: 'error',
              text1:
                error?.response?.data?.message || 'Failed to remove product',
            });
          } finally {
            dispatch(showPopup({ name: 'isLoading', value: false }));
          }
        }
      }

      const newProducts = products.filter((p) => {
        const currentComboId = p.product?.product_combination_id;
        return currentComboId !== combinationIdToRemove;
      });
      setProducts(newProducts);
      setConflictingProducts((prev) =>
        prev.filter((p) => p.combination?.id !== combinationIdToRemove),
      );

      if (newProducts.length === 0) {
        setShowProductExistModal(false);
      } else if (conflictingProducts.length === 1) {
        setShowProductExistModal(false);
      }
    }
  };

  const handleNavigateToConflictingRequest = (requestId) => {
    setShowProductExistModal(false);
    navigation.navigate('RequestProductDetailScreen', { id: requestId });
  };

  const generatePayload = (values) => {
    let addressData = addressOptions.find((a) => a.value === values.address);

    if (!addressData && values.address) {
      if (typeof values.address === 'object') {
        addressData = values.address;
      } else if (
        detailData?.address &&
        (detailData.address.id === values.address ||
          detailData.address._id === values.address)
      ) {
        addressData = detailData.address;
      }
    }

    addressData = addressData || {};

    const outletData =
      selectedOutlet ||
      outletOptions.find((o) => o.id === values.outlet) ||
      values.outlet ||
      {};

    let customerSource = values.customer || {};
    if (selectedCustomer) {
      const selectedId = getSafeId(selectedCustomer.id);
      const formId = getSafeId(customerSource.id || customerSource._id);
      if (selectedId && formId && selectedId === formId) {
        customerSource = selectedCustomer;
      }
    }

    const salesTeamSource = isMT(customerSource)
      ? values.sales_team && values.sales_team.id
        ? values.sales_team
        : customerSource.sales_team || {}
      : customerSource.sales_team || {};

    const payload = {
      customer: {
        id: getSafeId(customerSource.id),
        name: String(customerSource.name || ''),
        code: String(customerSource.customer_code || customerSource.code || ''),

        key_account_manager: (customerSource.key_account_manager || []).map(
          (kam) => ({
            id: getSafeId(kam.id),
            name: kam.name,
          }),
        ),
        account_manager: (customerSource.account_manager || []).map((am) => ({
          id: getSafeId(am.id),
          name: am.name,
        })),

        market_type: {
          id: getSafeId(customerSource.market_type?.id),
          cerebro_id: Number(customerSource.market_type?.cerebro_id || 0),
          code: String(customerSource.market_type?.code || ''),

          business_type: {
            id: getSafeId(customerSource.market_type?.business_type?.id),
            code: String(customerSource.market_type?.business_type?.code || ''),
            cerebro_id: Number(
              customerSource.market_type?.business_type?.cerebro_id || 0,
            ),

            account: {
              id: getSafeId(
                customerSource.market_type?.business_type?.account?.id,
              ),
              code: String(
                customerSource.market_type?.business_type?.account?.code || '',
              ),
              cerebro_id: Number(
                customerSource.market_type?.business_type?.account
                  ?.cerebro_id || 0,
              ),
            },
          },
        },

        sales_team: {
          id: getSafeId(salesTeamSource.id),
          name: String(salesTeamSource.name || ''),
          cerebro_id: Number(salesTeamSource.cerebro_id || 0),

          warehouse: {
            id: salesTeamSource.warehouse?.id,
            name: String(salesTeamSource.warehouse?.name || ''),
          },
        },

        branch: {
          code: String(customerSource.branch?.code || ''),
        },
      },
      outlet: {
        id: getSafeId(outletData.id || outletData._id),
        name: String(outletData.name || ''),
        code: String(outletData.code || ''),
        market_type: {
          id: getSafeId(outletData.market_type?.id),
          cerebro_id: Number(outletData.market_type?.cerebro_id || 0),
          code: String(outletData.market_type?.code || ''),
          business_type: {
            cerebro_id: Number(outletData.business_type?.cerebro_id || 0),
            code: String(outletData.business_type?.code || ''),
            id: getSafeId(outletData.business_type?.id),
          },
        },
      },
      address: {
        id: getSafeId(addressData.id || addressData._id),
        name: String(addressData.name || ''),
        street: String(addressData.street || ''),
        address_type: !customerSource?.is_sub_distributor ? addressType : null,
        country: {
          name: String(addressData.country?.name || ''),
          id: getSafeId(addressData.country?.id),
          cerebro_id: Number(addressData.country?.cerebro_id || 0),
        },
        state: {
          name: String(addressData.state?.name || ''),
          id: addressData.state?.id || '',
          cerebro_id: Number(addressData.state?.cerebro_id || 0),
        },
        city: {
          name: String(addressData.city?.name || ''),
          id: addressData.city?.id || '',
        },
        lat_long: {
          latitude: Number(addressData.lat_long?.latitude || 0),
          longitude: Number(addressData.lat_long?.longitude || 0),
        },
      },
      status: values.status || 'draft',
      note: values.note || '',
      request_product_type: 'testers',
      user: {
        id: getSafeId(values.request_by?._id || values.request_by?.id) || '',
        name: String(values.request_by?.name || values.request_by?.label || ''),
      },
    };

    if (!payload.outlet?.id) {
      delete payload.outlet;
    }

    return payload;
  };

  const handleSave = async (
    values,
    isFromProduct = false,
    productsOverride = null,
  ) => {
    const currentId = detailData?._id || id;

    dispatch(showPopup({ name: 'isLoading', value: true }));
    setActionLoading(true);
    setNewRptId(null);
    try {
      const currentProducts = productsOverride || products;
      const productToSend =
        currentProducts.length > 0
          ? isFromProduct
            ? currentProducts[currentProducts.length - 1]
            : currentProducts[0]
          : null;

      const payload = generatePayload(values);

      let res;
      if (currentId) {
        res = await MsOrder.updateRequestProductTester(currentId, payload);
      } else {
        res = await MsOrder.createRequestProductTester(payload);
        setNewRptId(res?.data?.data?._id || res?.data?.data?.id);

        if (isFromProduct && productToSend) {
          const newId = res?.data?.data?._id || res?.data?.data?.id;
          if (newId) {
            try {
              const productPayload = {
                product: {
                  id: productToSend.product?.id || productToSend.id,
                  combination_id:
                    productToSend.product?.product_combination_id ||
                    productToSend.product_combination_id,
                },
              };
              await MsOrder.addProductToRequestProductTester(
                newId,
                productPayload,
              );
            } catch (prodErr) {
              console.error(
                'Failed to add product after creating header',
                prodErr,
              );
              const errorCode = prodErr?.response?.data?.errorCode;
              if (errorCode === 'PRODUCT_ALREADY_EXISTS') {
                const errorData = prodErr?.response?.data?.data?.data;
                if (errorData?.products) {
                  setConflictingProducts(errorData.products);
                  setShowProductExistModal(true);
                  // We should stop here and not navigate away
                  return;
                }
              }

              Toast.show({
                type: 'error',
                text1:
                  'Request created but failed to add product. Please try adding it again.',
              });
            }
          }
        }
      }

      setShowRejectModal(false);
      setRejectNote('');
      Toast.show({
        type: 'success',
        text1: 'Request Product Tester saved successfully',
      });

      setTimeout(() => {
        if (isFromProduct) {
          if (!currentId) {
            const newId = res?.data?.data?._id || res?.data?.data?.id;
            if (newId) {
              navigation.replace('RequestProductFormScreen', { id: newId });
            } else {
              navigation.navigate('RequestProductListScreen');
            }
          } else {
            fetchDetail(currentId);
          }
        } else {
          const savedId = currentId || res?.data?.data?._id;
          if (savedId) {
            navigation.navigate('RequestProductDetailScreen', { id: savedId });
          } else {
            navigation.navigate('RequestProductListScreen');
          }
        }
      }, 1000);
    } catch (error) {
      const errorCode = error?.response?.data?.errorCode;
      if (errorCode === 'PRODUCT_ALREADY_EXISTS') {
        const errorData = error?.response?.data?.data?.data;
        if (errorData?.products) {
          setConflictingProducts(errorData.products);
          setShowProductExistModal(true);
        } else {
          Toast.show({
            type: 'error',
            text1: error?.response?.data?.message || 'Product already exists',
          });
        }
      } else {
        Toast.show({
          type: 'error',
          text1: error?.response?.data?.message || 'Failed to save',
        });
      }
    } finally {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      setActionLoading(false);
    }
  };

  const handleAddressTypeChange = (type, setFieldValue) => {
    setAddressType(type);
    const targetList = type === 'delivery' ? deliveryAddresses : storeAddresses;
    if (targetList.length > 0) {
      setFieldValue('address', targetList[0].value);
    } else {
      setFieldValue('address', null);
    }
  };

  const buildMobileButtons = (
    formValues,
    handleSubmit,
    setFieldValue,
    validateForm,
  ) => {
    if (page === 1) {
      return [
        {
          title: 'Next',
          type: 'primary',
          disabled: !(
            formValues?.customer?.id &&
            formValues?.address &&
            formValues?.sales_team?.id &&
            (formValues?.request_by?._id || formValues?.request_by?.id)
          ),
          onPress: (evt) => {
            setPage(2);
          },
        },
      ];
    }

    const buttons = [
      {
        title: 'Save Draft',
        type: 'primary',
        onPress: async (evt) => {
          if (evt?.persist) {
            evt.persist();
          }
          setFieldValue('status', 'draft');
          setTimeout(() => {
            validateForm().then((errors) => {
              if (Object.keys(errors).length > 0) {
                Toast.show({
                  type: 'error',
                  text1: 'Please fill all required fields',
                });
              }
              handleSubmit(evt);
            });
          }, 100);
        },
      },
    ];
    if (isEdit) {
      buttons.push({
        title: 'Request Confirmation',
        type: 'primary',
        onPress: async (evt) => {
          if (evt?.persist) {
            evt.persist();
          }
          setFieldValue('status', 'need_approval');
          setTimeout(() => {
            validateForm().then((errors) => {
              if (Object.keys(errors).length > 0) {
                Toast.show({
                  type: 'error',
                  text1: 'Please fill all required fields',
                });
              }
              handleSubmit(evt);
            });
          }, 100);
        },
      });
    }
    buttons.push({
      title: 'Back',
      type: 'default',
      onPress: (evt) => {
        setPage(1);
      },
    });

    return buttons;
  };

  const renderItem = ({ item, index }) => (
    <TouchableOpacity
      key={'list-product-' + index}
      style={{
        borderBottomColor: Colors.grey9,
        borderBottomWidth: 1,
        paddingVertical: 15,
        paddingHorizontal: 10,
        flexDirection: 'row',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
      <View style={{ marginRight: 10, alignSelf: 'flex-start' }}>
        <Image
          source={{ uri: `${item.image ? item.image : defaultPhoto}` }}
          style={{ width: 36, height: 36 }}
          width={36}
          height={36}
          defaultSource={defaultPhoto}
        />
      </View>
      <View style={{ flex: 1, paddingRight: 20 }}>
        <SociollaText
          type="medium"
          style={{ fontSize: 12, color: Colors.black1, paddingBottom: 8 }}>
          {item.brand.name}
        </SociollaText>
        <SociollaText
          style={{ fontSize: 12, color: Colors.black5, paddingBottom: 8 }}>
          {item.name}
        </SociollaText>
        <SociollaText
          type="medium"
          style={{ fontSize: 12, color: Colors.black1, paddingBottom: 8 }}>
          {item.combination.reference}
        </SociollaText>
        <SociollaText
          style={{ fontSize: 12, color: Colors.black5, paddingBottom: 8 }}>
          {item.variant}
        </SociollaText>
        <SociollaText style={{ fontSize: 12, color: Colors.black5 }}>
          Qty: {item.quantity}
        </SociollaText>
      </View>
      <View
        style={{
          height: '100%',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
        }}>
        <TouchableOpacity
          onPress={async () => await handleRemoveProduct(index)}>
          <Image
            width={20}
            height={20}
            svg={true}
            source={require('@assets/img/icons/trash-grey.svg')}
            style={{ height: 20, width: 20 }}
          />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  return (
    <Layout.Container>
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => handleSave(values)}>
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleSubmit,
          validateForm,
        }) => {
          const selectedAddressObj = addressOptions.find(
            (a) => a.value === values.address,
          );

          return (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false}>
                  <>
                    <Layout.Back
                      title="Request Product List"
                      onCallback={() => {
                        if (isEdit) {
                          navigation.navigate('RequestProductDetailScreen', {
                            id,
                          });
                        } else {
                          navigation.navigate('RequestProductListScreen');
                        }
                      }}
                    />
                    <Layout.Title
                      title={
                        isEdit
                          ? `Edit Request Product Tester ${
                              detailData?.code || ''
                            }`
                          : 'Create New Request Product Tester'
                      }>
                      <View style={{ flexDirection: 'row' }}>
                        {(() => {
                          const status = detailData?.status || 'draft';
                          const baseRoles = BASE_ROLES;

                          const canPerformBaseAction =
                            baseRoles.includes(userRole);

                          if (status === 'draft' || status === 'rejected') {
                            if (!canPerformBaseAction) {
                              return null;
                            }
                            return (
                              <>
                                <Button
                                  type="primary"
                                  buttonStyle={{ width: 105, marginRight: 10 }}
                                  title={
                                    <SociollaText
                                      type="bold"
                                      style={{
                                        fontSize: 14,
                                        letterSpacing: 0.5,
                                        color: Colors.white,
                                      }}>
                                      Save Draft
                                    </SociollaText>
                                  }
                                  onPress={(evt) => {
                                    if (evt?.persist) {
                                      evt.persist();
                                    }
                                    setFieldValue('status', 'draft');
                                    setTimeout(() => {
                                      validateForm().then((errors) => {
                                        if (Object.keys(errors).length > 0) {
                                          Toast.show({
                                            type: 'error',
                                            text1:
                                              'Please fill all required fields',
                                          });
                                        }
                                        handleSubmit(evt);
                                      });
                                    }, 100);
                                  }}
                                  // loading={actionLoading}
                                  disabled={actionLoading}
                                />
                                <RenderIf condition={isEdit}>
                                  <Button
                                    rounded
                                    buttonStyle={{ width: 185 }}
                                    disabledStyle={{ width: 185 }}
                                    title={
                                      <SociollaText
                                        type="bold"
                                        style={{
                                          fontSize: 14,
                                          letterSpacing: 0.5,
                                          color: Colors.white,
                                        }}>
                                        Request Confirmation
                                      </SociollaText>
                                    }
                                    onPress={(evt) => {
                                      if (evt?.persist) {
                                        evt.persist();
                                      }
                                      setFieldValue('status', 'need_approval');
                                      setTimeout(() => {
                                        validateForm().then((errors) => {
                                          if (Object.keys(errors).length > 0) {
                                            Toast.show({
                                              type: 'error',
                                              text1:
                                                'Please fill all required fields',
                                            });
                                          }
                                          handleSubmit(evt);
                                        });
                                      }, 100);
                                    }}
                                    // loading={actionLoading}
                                    disabled={actionLoading}
                                  />
                                </RenderIf>
                              </>
                            );
                          }

                          const canApprove = detailData?.current_approval
                            ? detailData.current_approval === userRole
                            : false;

                          if (canApprove) {
                            return (
                              <>
                                <Button
                                  type="outline"
                                  title="Reject"
                                  buttonStyle={{
                                    marginRight: 10,
                                    width: 120,
                                    borderColor: Colors.red,
                                  }}
                                  titleStyle={{ color: Colors.red }}
                                  onPress={() => setShowRejectModal(true)}
                                  loading={actionLoading}
                                  disabled={actionLoading}
                                />
                                <Button
                                  type="primary"
                                  title="Confirm"
                                  buttonStyle={{ width: 120 }}
                                  onPress={(evt) => {
                                    if (evt?.persist) {
                                      evt.persist();
                                    }
                                    setFieldValue(
                                      'status',
                                      getNextStatus(status),
                                    );
                                    setTimeout(() => {
                                      validateForm().then(() => {
                                        handleSubmit(evt);
                                      });
                                    }, 100);
                                  }}
                                  loading={actionLoading}
                                  disabled={actionLoading}
                                />
                              </>
                            );
                          }

                          return null;
                        })()}
                      </View>
                    </Layout.Title>
                  </>
                </Layout.Wrapper>
              ) : null}
              <ContentWrapper fill={!isWeb && page == 1}>
                <RenderIf condition={!isWeb}>
                  <ProgressBar page={page} totalPage={TOTAL_PAGE} />
                </RenderIf>
                <RenderIf condition={isWeb || (!isWeb && page == 1)}>
                  <FormBody
                    style={{
                      paddingHorizontal: isWeb ? 20 : 15,
                      paddingVertical: 20,
                      ...(!isWeb && !isMobileWeb
                        ? { paddingBottom: 160 }
                        : { paddingBottom: 40 }),
                    }}>
                    <View
                      style={{
                        flexDirection: isWeb ? 'row' : 'column',
                        flexWrap: isWeb ? 'wrap' : 'nowrap',
                        width: '100%',
                      }}>
                      {/* Left Column */}
                      <View
                        style={{
                          width: isWeb ? '50%' : '100%',
                          paddingRight: isWeb ? 15 : 0,
                        }}>
                        <Layout.FormGroup>
                          <CustomerSearch
                            fields="_id name customer_code market_type sales_team is_sub_distributor key_account_manager account_manager branch"
                            label="Customer*"
                            placeholder="Select Customer"
                            value={values.customer}
                            onChange={(val) =>
                              handleCustomerChange(val, setFieldValue)
                            }
                            error={touched.customer && errors.customer}
                            isDisabled={loading || products.length > 0}
                            customFilter={{
                              status: 'approved',
                              is_active: true,
                            }}
                          />
                        </Layout.FormGroup>

                        <Layout.FormGroup>
                          <Input
                            label="Customer Code*"
                            value={selectedCustomer?.customer_code || ''}
                            editable={false}
                          />
                        </Layout.FormGroup>

                        <Layout.FormGroup>
                          {isMT(selectedCustomer) ? (
                            <SalesTeamSearch
                              isRequired
                              value={
                                values.sales_team && values.sales_team.id
                                  ? values.sales_team
                                  : ''
                              }
                              hasError={
                                touched.sales_team && errors.sales_team
                                  ? errors.sales_team
                                  : null
                              }
                              customFilter={
                                userSalesTeam?.length
                                  ? {
                                      _id: {
                                        $in: userSalesTeam.map((st) => st.id),
                                      },
                                    }
                                  : {}
                              }
                              onChange={(value) =>
                                setFieldValue('sales_team', value)
                              }
                            />
                          ) : (
                            <Input
                              label="Sales Team*"
                              value={
                                values.sales_team && values.sales_team.id
                                  ? values.sales_team.name
                                  : ''
                              }
                              editable={false}
                              hasError={
                                touched.sales_team && errors.sales_team
                                  ? errors.sales_team
                                  : null
                              }
                            />
                          )}
                        </Layout.FormGroup>

                        {selectedCustomer?.is_sub_distributor && (
                          <>
                            <Layout.FormGroup>
                              <SelectBox
                                label="Outlet Name*"
                                placeholder="Select Outlet"
                                value={values.outlet}
                                options={outletOptions}
                                onChange={(val) =>
                                  handleOutletChange(val, setFieldValue)
                                }
                                error={touched.outlet && errors.outlet}
                                disabled={!values.customer}
                                searchable
                                trackBy="value"
                                labelKey="label"
                              />
                            </Layout.FormGroup>

                            <Layout.FormGroup>
                              <Input
                                label="Outlet Code*"
                                value={selectedOutlet?.code || ''}
                                editable={false}
                              />
                            </Layout.FormGroup>
                          </>
                        )}
                      </View>

                      {/* Right Column */}
                      <View
                        style={{
                          width: isWeb ? '50%' : '100%',
                          paddingLeft: isWeb ? 15 : 0,
                        }}>
                        {!selectedCustomer?.is_sub_distributor && (
                          <Layout.FormGroup style={{ gap: 10 }}>
                            <SociollaText
                              style={{
                                fontSize: 11,
                              }}>
                              Customer Address Type
                            </SociollaText>
                            <RadioButton
                              orientation="horizontal"
                              value={addressType}
                              data={addressTypeOptions}
                              onChange={(val) =>
                                handleAddressTypeChange(val, setFieldValue)
                              }
                              disabled={!values.customer}
                            />

                            <SelectBox
                              label={
                                addressType === 'store'
                                  ? 'Store Address*'
                                  : 'Delivery Address*'
                              }
                              placeholder="Select Address"
                              value={values.address}
                              options={
                                addressType === 'store'
                                  ? storeAddresses
                                  : deliveryAddresses
                              }
                              onChange={(val) =>
                                setFieldValue('address', val?.value)
                              }
                              error={touched.address && errors.address}
                              disabled={!values.customer || !addressType}
                              trackBy="value"
                              labelKey="name"
                              searchable
                            />
                          </Layout.FormGroup>
                        )}

                        <View style={{ marginBottom: 20, marginLeft: 10 }}>
                          <SociollaText
                            type="bold"
                            style={{ fontSize: 12, marginBottom: 5 }}>
                            Address
                          </SociollaText>
                          <SociollaText
                            style={{ fontSize: 14, color: Colors.grey2 }}>
                            {getAddressString(
                              selectedCustomer,
                              selectedOutlet,
                              selectedAddressObj,
                            )}
                          </SociollaText>
                        </View>

                        <Layout.FormGroup>
                          <Input
                            label="Request Product*"
                            value="Tester"
                            editable={false}
                          />
                        </Layout.FormGroup>

                        <Layout.FormGroup>
                          {canSelectUser ? (
                            <SelectBox
                              label="Request by*"
                              placeholder="Select User"
                              value={values.request_by}
                              options={usersOptions}
                              onChange={(val) => {
                                setFieldValue('request_by', val);
                              }}
                              error={touched.request_by && errors.request_by}
                              searchable
                              trackBy="value"
                              labelKey="name"
                              withCallback={true}
                              searchCallback={handleSearchUsers}
                              withLoadMore={true}
                              onLoadMore={handleLoadMoreUsers}
                              loadingCallback={isLoadMoreUsers}
                              loadMoreEmpty={loadMoreUsersEmpty}
                              limit={usersLimit}
                            />
                          ) : (
                            <Input
                              label="Request by*"
                              value={user?.name || ''}
                              editable={false}
                            />
                          )}
                        </Layout.FormGroup>
                      </View>
                    </View>
                  </FormBody>
                </RenderIf>

                <RenderIf condition={isWeb || (!isWeb && page == 2)}>
                  <View
                    style={{
                      backgroundColor: Colors.grey4,
                      paddingHorizontal: 20,
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                    <TitleSubSection
                      title={'PRODUCT'}
                      button={{
                        label: 'Add Product',
                        callback: handleAddProductClick,
                      }}
                      disabled={
                        !values.customer ||
                        !values.address ||
                        !values.request_by ||
                        !values.sales_team?.id ||
                        (selectedCustomer?.is_sub_distributor && !values.outlet)
                      }
                    />
                  </View>

                  <View style={{ paddingHorizontal: 20 }}>
                    <RenderIf condition={isWeb}>
                      <SociollaTable
                        table={HEADER_DEKSTOP_EDIT}
                        ref={tableRef}
                        datas={products}
                        showSearch={true}
                        searchBorder={true}
                        showPagination={true}
                        searchPlaceholder="Search by Name, Reference, EAN"
                        count={productCount}
                        loading={loading}
                        callBackAction={async ({ action, data, index }) => {
                          await handleRemoveProduct(index);
                        }}
                        onSearchTable={(val, type) => onSearchTable(val, type)}
                        localParams={paramsProduct}
                      />
                    </RenderIf>
                  </View>
                </RenderIf>

                <RenderIf
                  condition={
                    !isWeb &&
                    (isEdit ? !getRequestProductTesterDetail?.loading : true)
                  }>
                  <Layout.ButtonSticky
                    show={props?.route?.name == 'RequestProductFormScreen'}
                    route={props?.route}
                    buttons={buildMobileButtons(
                      values,
                      handleSubmit,
                      setFieldValue,
                      validateForm,
                    )}
                  />
                </RenderIf>
              </ContentWrapper>

              {/* Product List Mobile */}
              <RenderIf condition={!isWeb && page == 2}>
                <SociollaTable
                  datas={products}
                  count={productCount}
                  totalData={productCount}
                  itemComponent={renderItem}
                  loading={loading}
                  scrollItem={true}
                  searchPlaceholder="Search by Name, Reference, EAN"
                  loadingLoadMore={loadingMore}
                  onSearchTable={(val, type) => onSearchTable(val, type)}
                  localParams={paramsProduct}
                  styleContainer={{ boxShadow: 'none' }}
                  headerComponent={<></>}
                  // The Android branch was 90 higher to fake the status and nav
                  // bars back when the edge-to-edge opt-out kept insets at 0.
                  // SociollaTable subtracts the real insets now, so the extra
                  // would be counted twice.
                  minusHeight={310}
                  keyExtractor={(item) => item._id.toString()}
                />
              </RenderIf>
            </>
          );
        }}
      </Formik>

      <ModalProduct
        show={showAddProduct}
        onClose={() => onCloseProduct()}
        onChange={handleProductAdd}
        isCatalog={false}
        customerId={selectedCustomer?._id}
      />

      <Modal
        animationType="fade"
        transparent={true}
        visible={showRejectModal}
        onRequestClose={() => setShowRejectModal(false)}>
        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0,0,0,0.5)',
          }}>
          <View
            style={{
              width: '80%',
              backgroundColor: 'white',
              padding: 20,
              borderRadius: 10,
            }}>
            <SociollaText
              type="bold"
              style={{ marginBottom: 10, fontSize: 16 }}>
              Reject Request
            </SociollaText>
            <TextInput
              style={{
                borderWidth: 1,
                borderColor: Colors.grey2,
                borderRadius: 4,
                padding: 10,
                height: 100,
                textAlignVertical: 'top',
                marginBottom: 20,
              }}
              multiline
              placeholder={translate('Enter reject reason (optional)')}
              value={rejectNote}
              onChangeText={setRejectNote}
            />
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
              <Button
                type="outline"
                title="Cancel"
                onPress={() => setShowRejectModal(false)}
                buttonStyle={{ marginRight: 10, width: 100 }}
              />
              <Button
                type="primary"
                title="Reject"
                onPress={() => {
                  if (formRef.current) {
                    formRef.current.setFieldValue('status', 'rejected');
                    formRef.current.setFieldValue('note', rejectNote);
                    setTimeout(() => formRef.current.handleSubmit(), 100);
                  }
                }}
                buttonStyle={{ width: 100 }}
                loading={actionLoading}
              />
            </View>
          </View>
        </View>
      </Modal>
      <ModalProductExist
        show={showProductExistModal}
        onClose={() => {
          setShowProductExistModal(false);
          if (!isEdit && newRptId) {
            navigation.replace('RequestProductFormScreen', { id: newRptId });
          }
        }}
        products={conflictingProducts}
        onRemoveProduct={handleRemoveConflictingProduct}
        onNavigateDetail={handleNavigateToConflictingRequest}
      />
    </Layout.Container>
  );
};

export default RequestProductFormScreen;
